import _ from 'lodash';
import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Radio, Dropdown, Icon } from 'semantic-ui-react';
import {
  Loader,
  captureException,
  IfScope,
} from '@playbookux/playbook-web-core';
import { useMutation, useQuery } from '@apollo/client';
import { mapCountries } from '../../../../../constants/countries';
import { demographicsAttributes, questionTypeMatrix } from '../constants';
import {
  Header,
  SavedFilterContainer,
  SavedFilterTopRow,
  SavedFilterLeftContainer,
  ThreeDotDropdown,
  FilterConditions,
  FilterName,
} from './styles';
import {
  GET_SAVED_FILTERS,
  DELETE_ANALYTICS_FILTER,
} from '../../graphql';

const SavedFiltersTab = ({
  projectId,
  allQuestions,
  scope,
  setApplyFunction,
  onFilterApplied,
}) => {
  const [selectedFilterId, setSelectedFilterId] = useState(null);

  const { data: savedFiltersData, loading: loadingSavedFilters } = useQuery(GET_SAVED_FILTERS, {
    variables: { projectId },
  });

  const [deleteFilter] = useMutation(DELETE_ANALYTICS_FILTER, {
    refetchQueries: [{ query: GET_SAVED_FILTERS, variables: { projectId } }],
  });

  const handleDeleteFilter = async (id) => {
    try {
      await deleteFilter({ variables: { id } });
    } catch (err) {
      captureException('DELETE_ANALYTICS_FILTER', err);
    }
  };

  const handleApplySavedFilter = async () => {
    const selectedFilter = savedFiltersData.analyticsFilters.find((filter) => filter.id === selectedFilterId);
    if (selectedFilter) {
      const savedConditions = JSON.parse(selectedFilter.conditions);
      onFilterApplied({
        id: selectedFilter.id,
        name: selectedFilter.name,
        conditions: savedConditions,
        saved: true,
      });
    }
  };

  useEffect(() => {
    setApplyFunction({
      canApply: !!selectedFilterId,
      apply: handleApplySavedFilter,
    });
  }, [selectedFilterId, setApplyFunction]);

  if (loadingSavedFilters) {
    return <Loader data-loader="saved-filters-loader" />;
  }

  const formatAttribute = (attribute) => {
    if (demographicsAttributes.includes(attribute)) {
      return _.startCase(attribute);
    }
    const question = allQuestions.find((q) => q.id === attribute);
    return `${question.label} (${_.startCase(question.questionType)})`;
  };

  const formatConditionValue = (values) => {
    if (!Array.isArray(values)) {
      return values;
    }
    return values.length === 1 ? values[0] : values.join(', ');
  };

  const getMatrixLabel = (question, val) => {
    const rowId = Object.keys(val)[0];
    const colId = val[rowId];

    // Find the corresponding questionOptions
    const rowOption = question.questionOptions.find((o) => o.id === rowId);
    const colOption = question.questionOptions.find((o) => o.id === colId);

    // Constructs the string "RowLabel - ColLabel"
    const rowLabel = rowOption ? rowOption.label : rowId;
    const colLabel = colOption ? colOption.label : colId;
    return `${rowLabel} - ${colLabel}`;
  };

  const formatValue = (condition) => {
    if (demographicsAttributes.includes(condition.attribute)) {
      const conditionValue = condition.attribute === 'country'
        ? condition.value.map((countryCode) => mapCountries[countryCode])
        : condition.value;

      return formatConditionValue(conditionValue);
    }
    return condition.value
      .map((opt) => {
        const question = allQuestions.find((q) => q.id === condition.attribute);
        if (question.questionType === questionTypeMatrix) {
          return getMatrixLabel(question, opt);
        }
        const optionValue = question.questionOptions.find((o) => o.id === opt);
        if (optionValue) return optionValue.label;
        return opt;
      })
      .join(', ');
  };

  return (
    <>
      <Header as="h4">Select a saved filter</Header>
      {savedFiltersData.analyticsFilters.length > 0 ? (
        savedFiltersData.analyticsFilters.map((filter) => (
          <div key={filter.id}>
            <SavedFilterContainer>
              <SavedFilterTopRow>
                <SavedFilterLeftContainer>
                  <Radio
                    label=""
                    name="savedFilterRadioGroup"
                    checked={selectedFilterId === filter.id}
                    onChange={() => setSelectedFilterId(filter.id)}
                  />
                  <FilterName>{filter.name}</FilterName>
                </SavedFilterLeftContainer>
                <IfScope user scope={scope}>
                  <ThreeDotDropdown>
                    <Dropdown
                      trigger={<Icon name="ellipsis vertical" />}
                      pointing="top right"
                      icon={null}
                    >
                      <Dropdown.Menu>
                        <Dropdown.Item
                          text="Delete"
                          onClick={() => handleDeleteFilter(filter.id)}
                        />
                      </Dropdown.Menu>
                    </Dropdown>
                  </ThreeDotDropdown>
                </IfScope>
              </SavedFilterTopRow>
              <FilterConditions>
                {JSON.parse(filter.conditions)
                  .map((condition) => {
                    const operatorFormatted = _.lowerCase(condition.operator);

                    return `${formatAttribute(condition.attribute)} ${operatorFormatted} ${formatValue(condition)}`;
                  })
                  .join(', ')}
              </FilterConditions>
            </SavedFilterContainer>
          </div>
        ))
      ) : (
        <p>No saved filters.</p>
      )}
    </>
  );
};

SavedFiltersTab.propTypes = {
  projectId: PropTypes.string.isRequired,
  allQuestions: PropTypes.array.isRequired,
  scope: PropTypes.string.isRequired,
  setApplyFunction: PropTypes.func.isRequired,
  onFilterApplied: PropTypes.func.isRequired,
};

export default SavedFiltersTab;
